import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import type { NetworkDevice, DeviceStatus, RouteRecord } from '../types';

interface GraphNode {
  id: string;
  dev_id: string;
  name: string;
  status: DeviceStatus;
  is_coordinator: boolean;
  has_children: boolean;
  sensor_count: number;
  device: NetworkDevice;
  x?: number;
  y?: number;
}

interface GraphLink {
  source: string | GraphNode;
  target: string | GraphNode;
  rssi: number;
  time: string;
  is_stale: boolean;
}

interface TopologyGraphProps {
  devices: NetworkDevice[];
  routeRecords: RouteRecord[];
  onNodeClick: (device: NetworkDevice) => void;
  selectedDeviceId: string | null;
}

const STATUS_COLORS: Record<DeviceStatus, string> = {
  online: '#22c55e',
  stale: '#f59e0b',
  offline: '#ef4444',
};

const FIFTEEN_MIN = 15 * 60 * 1000;

/** RSSI 0-7 signal quality color: green (7) -> amber (4) -> red (1) */
function rssiColor(rssi: number): string {
  if (rssi >= 6) return '#22c55e'; // green
  if (rssi >= 4) return '#f59e0b'; // amber
  if (rssi >= 2) return '#f97316'; // orange
  return '#ef4444'; // red
}

/** Draw signal bars icon at (cx, cy). 4 bars max, filled based on 0-7 RSSI mapped to 0-4. */
function drawSignalBars(ctx: CanvasRenderingContext2D, cx: number, cy: number, rssi: number, scale: number) {
  const barWidth = 2.5 / scale;
  const gap = 1.5 / scale;
  const maxBars = 4;
  const filledBars = Math.round((rssi / 7) * maxBars);
  const totalWidth = maxBars * barWidth + (maxBars - 1) * gap;
  const startX = cx - totalWidth / 2;
  const baseY = cy + 4 / scale;
  const color = rssiColor(rssi);

  for (let i = 0; i < maxBars; i++) {
    const barHeight = ((i + 1) / maxBars) * (10 / scale);
    const x = startX + i * (barWidth + gap);
    const y = baseY - barHeight;

    ctx.fillStyle = i < filledBars ? color : '#334155';
    ctx.fillRect(x, y, barWidth, barHeight);
  }

  // Label below bars
  const fontSize = Math.max(8 / scale, 1.5);
  ctx.font = `${fontSize}px sans-serif`;
  ctx.fillStyle = '#94a3b8';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(`${rssi}/7`, cx, baseY + 2 / scale);
}

export function TopologyGraph({ devices, routeRecords, onNodeClick, selectedDeviceId }: TopologyGraphProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const graphRef = useRef<any>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  const graphData = useMemo(() => {
    const deviceByDevId = new Map(devices.map(d => [d.dev_id, d]));

    const devIdsWithChildren = new Set<string>();
    for (const d of devices) {
      for (const gw of d.current_parent_gateways) {
        devIdsWithChildren.add(gw.mac);
      }
    }

    const nodes: GraphNode[] = devices.map(d => ({
      id: d._id,
      dev_id: d.dev_id,
      name: d.name,
      status: d.status,
      is_coordinator: d.is_coordinator,
      has_children: devIdsWithChildren.has(d.dev_id),
      sensor_count: d.sensor_count,
      device: d,
    }));

    const links: GraphLink[] = [];
    for (const d of devices) {
      for (const gw of d.current_parent_gateways) {
        const parentDevice = deviceByDevId.get(gw.mac);
        if (parentDevice) {
          const isStale = gw.time ? (Date.now() - new Date(gw.time).getTime()) > FIFTEEN_MIN : true;
          links.push({
            source: d._id,
            target: parentDevice._id,
            rssi: gw.rssi,
            time: gw.time,
            is_stale: isStale,
          });
        }
      }
    }

    // Add multi-hop links from route records (override gateway links when available)
    const routeLinkedDevices = new Set<string>();
    for (const route of routeRecords) {
      if (route.relayPath.length === 0) continue;

      const sourceDevice = devices.find(d => d.xbee_SL === route.sourceSl);
      if (!sourceDevice) continue;

      routeLinkedDevices.add(sourceDevice._id);

      // Build hop chain: source -> relay1 -> relay2 -> ... -> coordinator
      const hopChain = [sourceDevice._id];
      for (const myAddr of route.relayPath) {
        const relayDevice = devices.find(d => d.dev_id === myAddr || d.xbee_SL?.endsWith(myAddr));
        if (relayDevice) hopChain.push(relayDevice._id);
      }
      if (route.coordinatorDevId) {
        const coord = devices.find(d => d.dev_id === route.coordinatorDevId);
        if (coord && !hopChain.includes(coord._id)) hopChain.push(coord._id);
      }

      for (let i = 0; i < hopChain.length - 1; i++) {
        links.push({
          source: hopChain[i],
          target: hopChain[i + 1],
          rssi: route.rssi ?? 0,
          time: route.recordedAt,
          is_stale: (Date.now() - new Date(route.recordedAt).getTime()) > FIFTEEN_MIN,
        });
      }
    }

    // Remove gateway-based links for devices that have route record data
    const filteredLinks = links.filter(l => {
      const sourceId = typeof l.source === 'string' ? l.source : (l.source as GraphNode).id;
      return !routeLinkedDevices.has(sourceId);
    });

    return { nodes, links: filteredLinks };
  }, [devices, routeRecords]);

  useEffect(() => {
    if (graphRef.current && graphData.nodes.length > 0) {
      setTimeout(() => graphRef.current?.zoomToFit(400, 60), 300);
    }
  }, [graphData]);

  const nodeSize = useCallback((node: GraphNode) => {
    return Math.min(6 + node.sensor_count * 2, 18);
  }, []);

  const drawNode = useCallback((node: GraphNode, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const size = nodeSize(node);
    const color = STATUS_COLORS[node.status];
    const isSelected = node.id === selectedDeviceId;

    ctx.beginPath();

    if (node.is_coordinator) {
      const a = size;
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i - Math.PI / 6;
        const x = (node.x ?? 0) + a * Math.cos(angle);
        const y = (node.y ?? 0) + a * Math.sin(angle);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
    } else if (node.has_children && !node.is_coordinator) {
      const s = size;
      ctx.moveTo(node.x ?? 0, (node.y ?? 0) - s);
      ctx.lineTo((node.x ?? 0) + s, node.y ?? 0);
      ctx.lineTo(node.x ?? 0, (node.y ?? 0) + s);
      ctx.lineTo((node.x ?? 0) - s, node.y ?? 0);
      ctx.closePath();
    } else {
      ctx.arc(node.x ?? 0, node.y ?? 0, size, 0, 2 * Math.PI);
    }

    ctx.fillStyle = color;
    ctx.fill();

    const isHovered = node.id === hoveredNode;
    if (isSelected || isHovered) {
      ctx.strokeStyle = isSelected ? '#ffffff' : '#94a3b8';
      ctx.lineWidth = 2 / globalScale;
      ctx.stroke();
    }

    const label = node.name;
    const fontSize = Math.max(10 / globalScale, 2);
    ctx.font = `${fontSize}px sans-serif`;
    ctx.fillStyle = '#e2e8f0';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(label, node.x ?? 0, (node.y ?? 0) + size + 2);
  }, [selectedDeviceId, hoveredNode, nodeSize]);

  const drawLink = useCallback((link: GraphLink, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const source = link.source as GraphNode;
    const target = link.target as GraphNode;
    if (!source.x || !target.x) return;

    const rssi = link.rssi ?? 0;
    const thickness = Math.max(0.5, (rssi / 7) * 2) / globalScale;

    ctx.beginPath();
    ctx.moveTo(source.x, source.y ?? 0);

    if (link.is_stale) {
      ctx.setLineDash([4 / globalScale, 4 / globalScale]);
    } else {
      ctx.setLineDash([]);
    }

    ctx.lineTo(target.x, target.y ?? 0);
    ctx.strokeStyle = link.is_stale ? '#64748b' : rssiColor(rssi);
    ctx.lineWidth = thickness;
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw signal bars at midpoint
    if (rssi != null) {
      const midX = ((source.x) + (target.x)) / 2;
      const midY = ((source.y ?? 0) + (target.y ?? 0)) / 2;
      drawSignalBars(ctx, midX, midY, rssi, globalScale);
    }
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleNodeClick = useCallback((node: any) => {
    if (node?.device) onNodeClick(node.device);
  }, [onNodeClick]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleNodeHover = useCallback((node: any) => {
    setHoveredNode(node?.id ?? null);
  }, []);

  const linkLabel = useCallback((link: GraphLink) => {
    const rssi = link.rssi;
    const quality = rssi == null ? 'Unknown'
      : rssi >= 6 ? 'Excellent' : rssi >= 4 ? 'Good' : rssi >= 2 ? 'Weak' : 'Critical';
    const rssiLabel = rssi != null ? `Signal: ${rssi}/7 (${quality})` : 'Signal: unknown';
    const timeLabel = link.time ? `Last: ${new Date(link.time).toLocaleString('en-CA', { timeZone: 'America/Montreal' })}` : '';
    return `${rssiLabel}${timeLabel ? ` | ${timeLabel}` : ''}`;
  }, []);

  return (
    <ForceGraph2D
      ref={graphRef}
      graphData={graphData}
      nodeCanvasObject={drawNode}
      nodePointerAreaPaint={(node: GraphNode, color: string, ctx: CanvasRenderingContext2D) => {
        const size = nodeSize(node);
        ctx.beginPath();
        // Generous hit area — 3x the visual node size for easy clicking
        ctx.arc(node.x ?? 0, node.y ?? 0, size * 3, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.fill();
      }}
      linkCanvasObject={drawLink}
      linkLabel={linkLabel}
      onNodeClick={handleNodeClick}
      onNodeHover={handleNodeHover}
      enableNodeDrag={false}
      enablePointerInteraction={true}
      backgroundColor="#0f172a"
      cooldownTicks={100}
      d3AlphaDecay={0.02}
      d3VelocityDecay={0.3}
    />
  );
}
